import {SocketApi, SocketApiAckStatus} from "proto_socket_typescript";
import {makeAutoObservable, reaction} from "mobx";
import {toast} from "react-toastify";
import {proto} from "../proto/messages";
import {reservations} from "../proto/compiled";
import {MladiVoznikStore} from "./mladi_voznik_store";
import {
    dismissReservationWarningToast,
    ShowReservationExpiredDialog,
    ShowReservationExpiringDialog,
    showReservationWarningToast,
} from "../components/reservation_dialogs";

/** Indeks 2. koraka (izbira termina) v MladiVoznikStore.stepList. */
const STEP_CALENDAR = 1;

/** Zadnja minuta - takrat opozorilo postane modalno. */
const CRITICAL_MS = 60 * 1000;

export type ReservationLevel = 'ok' | 'warn' | 'critical';

/**
 * Rezervacija mest na izbranem terminu.
 *
 * O poteku ne odloča odjemalec: odštevalnik teče do roka, ki ga je poslal strežnik, ob
 * 0:00 pa ta vpraša strežnik za avtoritativno stanje. Ura odjemalca je lahko zamaknjena,
 * zato vsak odgovor nosi tudi `serverTime` in iz nje izračunamo odmik.
 */
export class ReservationStore {
    api: SocketApi;
    base: MladiVoznikStore;
    subscriptions: any[] = [];
    disposers: (() => void)[] = [];

    lectureId?: string;
    places = 0;
    expiresAtMs?: number;
    extensionsLeft = 0;
    extendAvailableBeforeMs = 5 * 60 * 1000;
    /** serverTime - clientTime; popravek za zamaknjeno uro odjemalca */
    serverOffsetMs = 0;
    /** posodablja ga ticker, da so `remainingMs` in odvisni getterji reaktivni */
    nowMs = Date.now();
    extending = false;
    /** zahtevek za rezervacijo je v teku - prepreči dvojni klik na termin */
    reserving = false;
    lastFailure?: reservations.IReservationFailed;

    private ticker?: any;
    private pageHideHandler?: () => void;
    private warnedToast = false;
    private warnedModal = false;
    /** true, ko rezervacijo sprostimo sami - takrat ni "potekla", ampak preklicana */
    private releasing = false;

    constructor(base: MladiVoznikStore, api: SocketApi) {
        this.base = base;
        this.api = api;

        this.subscriptions.push(api.getMessageHandler(new proto.RxReservation()).subscribe(
            (m) => this.onReservation(m)
        ));
        this.subscriptions.push(api.getMessageHandler(new proto.RxReservationFailed()).subscribe(
            (m) => this.lastFailure = m.proto
        ));

        // Sprememba števila otrok se mora takoj poznati na rezerviranih mestih. Debounce,
        // da tipkanje "1" → "12" ne sproži dveh zahtevkov.
        this.disposers.push(reaction(
            () => this.base.form.step1?.children ?? 0,
            () => this.syncPlaces(),
            {delay: 400},
        ));

        this.pageHideHandler = () => this.releaseSync();
        window.addEventListener('pagehide', this.pageHideHandler);

        makeAutoObservable(this);
    }

    dispose() {
        this.releaseSync();
        this.stopTicker();
        if (this.pageHideHandler) window.removeEventListener('pagehide', this.pageHideHandler);
        for (const sub of this.subscriptions) sub.unsubscribe();
        for (const d of this.disposers) d();
        dismissReservationWarningToast();
    }

    get validationCode(): string | undefined {
        return this.base.step1.result?.validationCode ?? undefined;
    }

    get requiredPlaces(): number {
        return this.base.childrenMultiplier;
    }

    get remainingMs(): number {
        if (!this.expiresAtMs) return 0;
        return this.expiresAtMs - (this.nowMs + this.serverOffsetMs);
    }

    get active(): boolean {
        return !!this.expiresAtMs && this.remainingMs > 0;
    }

    get level(): ReservationLevel {
        if (this.remainingMs <= CRITICAL_MS) return 'critical';
        if (this.remainingMs <= this.extendAvailableBeforeMs) return 'warn';
        return 'ok';
    }

    /** Podaljšanje ponudimo šele proti koncu - sicer bi ga uporabniki klikali takoj. */
    get canExtend(): boolean {
        return this.active && this.extensionsLeft > 0 && this.remainingMs <= this.extendAvailableBeforeMs;
    }

    get remainingLabel(): string {
        const total = Math.max(0, Math.ceil(this.remainingMs / 1000));
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    /** Rezervira mesta na terminu. `false` pomeni, da termin ni (več) na voljo. */
    async reserve(lectureId?: string | null, places?: number): Promise<boolean> {
        // Brez prijavljene seje rezervacije ni (npr. tečaj brez 1. koraka) - obrazca
        // zaradi tega ne blokiramo, kapaciteto še vedno preveri strežnik ob oddaji.
        if (!this.validationCode || !lectureId) return true;
        this.lastFailure = undefined;
        this.reserving = true;
        try {
            const response = await this.api.sendMessage(proto.TxReserveSlot.create({
                validationCode: this.validationCode,
                lectureId: lectureId,
                places: places ?? this.requiredPlaces,
            }), {ack: true});
            return response.status === SocketApiAckStatus.success;
        } finally {
            this.reserving = false;
        }
    }

    async extend(): Promise<void> {
        if (!this.validationCode || this.extending) return;
        this.extending = true;
        const response = await this.api.sendMessage(proto.TxExtendReservation.create({
            validationCode: this.validationCode,
        }), {ack: true});
        this.extending = false;
        if (response.status === SocketApiAckStatus.success) {
            dismissReservationWarningToast();
            toast.success('Čas za prijavo je podaljšan.');
        } else {
            toast.error(response.errorMessage ?? 'Časa za prijavo ni bilo mogoče podaljšati.');
        }
    }

    release() {
        if (!this.validationCode || !this.expiresAtMs) return;
        this.releasing = true;
        this.api.sendMessage(proto.TxReleaseReservation.create({
            validationCode: this.validationCode,
        }));
        this.clear();
    }

    /** Sprostitev ob zapuščanju strani - brez čakanja na povezavo ali odgovor. */
    private releaseSync() {
        if (!this.validationCode || !this.expiresAtMs) return;
        this.releasing = true;
        this.api.sendMessage(proto.TxReleaseReservation.create({
            validationCode: this.validationCode,
        }), {waitConnected: false});
        this.clear();
    }

    /** Prijava je oddana - mesta zdaj drži prijava sama, odštevalnik ni več smiseln. */
    markConfirmed() {
        this.clear();
    }

    /** Avtoritativno stanje s strežnika. */
    private refresh() {
        if (!this.validationCode) return;
        this.api.sendMessage(proto.TxLoadReservation.create({
            validationCode: this.validationCode,
        }));
    }

    private clear() {
        this.stopTicker();
        dismissReservationWarningToast();
        this.lectureId = undefined;
        this.places = 0;
        this.expiresAtMs = undefined;
        this.warnedToast = false;
        this.warnedModal = false;
    }

    private onReservation(m: proto.RxReservation) {
        const p = m.proto;
        if (p.serverTime?.ts) {
            this.serverOffsetMs = (p.serverTime.ts as number) - Date.now();
        }
        if (!p.active || !p.expiresAt?.ts) {
            const wasActive = !!this.expiresAtMs;
            const selfInflicted = this.releasing;
            this.releasing = false;
            this.clear();
            if (wasActive && !selfInflicted) this.onExpired();
            return;
        }
        this.releasing = false;
        this.lectureId = p.lectureId ?? undefined;
        this.places = p.places ?? 0;
        this.expiresAtMs = p.expiresAt.ts as number;
        this.extensionsLeft = p.extensionsLeft ?? 0;
        if (p.extendAvailableBeforeS) {
            this.extendAvailableBeforeMs = p.extendAvailableBeforeS * 1000;
        }
        this.nowMs = Date.now();
        // po podaljšanju morata opozorili spet delovati
        if (this.remainingMs > this.extendAvailableBeforeMs) {
            this.warnedToast = false;
            this.warnedModal = false;
            dismissReservationWarningToast();
        }
        if (this.remainingMs > CRITICAL_MS) this.warnedModal = false;
        this.startTicker();
    }

    private startTicker() {
        if (this.ticker) return;
        this.ticker = setInterval(() => this.tick(), 1000);
    }

    private stopTicker() {
        if (!this.ticker) return;
        clearInterval(this.ticker);
        this.ticker = undefined;
    }

    private tick() {
        this.nowMs = Date.now();
        if (!this.expiresAtMs) {
            this.stopTicker();
            return;
        }
        const remaining = this.remainingMs;
        if (remaining <= 0) {
            // o poteku ne odločamo sami - vprašamo strežnik
            this.stopTicker();
            this.refresh();
            return;
        }
        if (remaining <= CRITICAL_MS && !this.warnedModal) {
            this.warnedModal = true;
            this.warnedToast = true;
            dismissReservationWarningToast();
            ShowReservationExpiringDialog({
                canExtend: this.extensionsLeft > 0,
                onExtend: () => this.extend(),
            });
        } else if (remaining <= this.extendAvailableBeforeMs && !this.warnedToast) {
            this.warnedToast = true;
            showReservationWarningToast({
                minutes: Math.round(this.extendAvailableBeforeMs / 60000),
                canExtend: this.extensionsLeft > 0,
                onExtend: () => this.extend(),
            });
        }
    }

    /**
     * Rezervirana mesta uskladi s trenutnim številom otrok. Zmanjšanje vedno uspe in
     * mesta takoj sprosti; povečanje lahko pade, če jih na terminu ni dovolj.
     */
    private async syncPlaces() {
        if (!this.active || !this.validationCode) return;
        const places = this.requiredPlaces;
        if (places === this.places) return;

        const response = await this.api.sendMessage(proto.TxResizeReservation.create({
            validationCode: this.validationCode,
            places: places,
        }), {ack: true});
        if (response.status === SocketApiAckStatus.success) return;

        const available = this.lastFailure?.placesAvailable ?? 0;
        toast.error(
            `Na izbranem terminu ni dovolj prostih mest (na voljo: ${available}). ` +
            `Izberite drug termin.`
        );
        this.release();
        this.backToCalendar();
    }

    private async onExpired() {
        // B2B termin je fiksen - uporabnika nima smisla pošiljati na izbiro, najprej
        // poskusimo isti termin rezervirati znova.
        const fixed = this.base.step2.fixedLecture;
        if (fixed?.id && await this.reserve(fixed.id, this.requiredPlaces)) return;

        ShowReservationExpiredDialog();
        this.backToCalendar();
    }

    /** Termin ni več naš: počisti izbiro, vrni na 2. korak in osveži razpoložljivost. */
    private backToCalendar() {
        this.base.form.step2!.date = undefined;
        this.base.step2.freePlaces = undefined;
        if (this.base.doneUpToStep > STEP_CALENDAR) this.base.doneUpToStep = STEP_CALENDAR;
        if (this.base.step !== STEP_CALENDAR) this.base.setStep(STEP_CALENDAR);
        this.base.step2.reloadCalendar();
    }
}